import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the WorkMap module
 */
export default class WorkMap extends MessageBus {
  /**
   * Constructor function for the WorkMap class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.cacheDomReferences().setupHandlers().enable().initializeTooltips();
  }

  /**
   * Cache DOM References
   * @returns {WorkMap} A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.mapContainer = this.element;
    this.mapCon = this.element.querySelector(`.${CLASS_NAMES.MAP_CONTAINER}`);
    this.countryElements = this.mapContainer.querySelectorAll(
      CLASS_NAMES.MAP_COUNTRY
    );
    this.overlay = this.mapContainer.querySelector(
      `.${CLASS_NAMES.MAP_OVERLAY}`
    );
    this.map = this.mapContainer.querySelector(CLASS_NAMES.MAP_SVG);
    this.mapCountry = this.mapContainer.querySelector(
      CLASS_NAMES.MAP_COUNTRY_COUNTRY
    );
    this.checkboxElements = this.element.querySelectorAll(
      CLASS_NAMES.MAP_CHECKBOX
    );
    this.landPaths = this.element.querySelectorAll(CLASS_NAMES.MAP_LAND_PATH);
    this.tooltip = this.element.querySelector(`#${CLASS_NAMES.MAP_TOOLTIP}`);

    return this;
  }

  /**
   * Setup event handlers
   * @returns {WorkMap} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    this.handleResize = this.handleResize.bind(this);
    return this;
  }

  /**
   * Enable event listeners and features
   * @returns {WorkMap} A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.checkboxElements.forEach((checkbox) => {
      const checkboxItem = checkbox;
      checkboxItem.checked = true;
      checkbox.addEventListener(EVENTS.CHANGE, this.handleCheckboxChange);
    });

    this.processCountriesMarkerOnLoad();
    this.processCountriesHighlightOnLoad();

    window.addEventListener(EVENTS.RESIZE, this.handleResize);

    return this;
  }

  /**
   * Process country elements on page load and place crisis markers.
   */
  processCountriesMarkerOnLoad() {
    this.overlay.innerHTML = '';
    const mapBounds = this.mapCon.getBoundingClientRect();

    this.countryElements.forEach((country) => {
      const iso = country.getAttribute('data-iso');
      const isCrisis = country.dataset.crisis === 'true';

      if (isCrisis) {
        const countryElement = this.map.querySelector(`#${iso}`);

        if (countryElement) {
          let largestBoundSubArea = 0;
          let countryBounds = countryElement.getBoundingClientRect();
          countryElement.querySelectorAll('path').forEach((path) => {
            const bounds = path.getBoundingClientRect();
            const boundSubArea = bounds.width * bounds.height;

            if (boundSubArea > largestBoundSubArea) {
              largestBoundSubArea = boundSubArea;
              countryBounds = bounds;
            }
          });

          // Create the markers
          const marker = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'svg'
          );
          marker.setAttribute('id', `crisis-marker-${iso}`);
          marker.classList.add('work-map__crisis-marker');
          marker.innerHTML =
            '<image href="./wp-content/themes/careorg/assets/icons/crisis-marker.svg"/>';

          const mapBoundsLeft = countryBounds.left - mapBounds.left;
          const mapCountryBoundsWidth = countryBounds.width / 2;
          const mapLeftPosition = mapBoundsLeft + mapCountryBoundsWidth;

          const mapBoundsTop = countryBounds.top - mapBounds.top;
          const mapCountryBoundsTop = countryBounds.height / 2;
          const mapTopPosition = mapBoundsTop + mapCountryBoundsTop;

          marker.style.position = 'absolute';
          marker.style.left = `${mapLeftPosition}px`;
          marker.style.top = `${mapTopPosition}px`;

          this.overlay.appendChild(marker);
        }
      }
    });
  }

  /**
   * Highlights countries on the map based on their `data-highlight-country-type` attribute.
   * Applies a CSS class for styling purposes.
   */
  processCountriesHighlightOnLoad() {
    this.countryElements.forEach((country) => {
      const iso = country.getAttribute('data-iso');
      const highlightCountryType = country.getAttribute(
        'data-highlight-country-type'
      );
      const countrySvg = this.element
        .querySelector(CLASS_NAMES.MAP_COUNTRY_COUNTRY)
        .querySelector(`#${iso}`);
      countrySvg.setAttribute(
        'data-highlight-country-type',
        highlightCountryType
      );
      countrySvg.classList.add(highlightCountryType);
    });
  }

  /**
   * Handles the change event for checkboxes to toggle map highlights and overlays.
   * @param {Event} event - The change event triggered by a checkbox.
   */
  handleCheckboxChange(event) {
    const checkbox = event.target;
    const isNowChecked = checkbox.checked;
    const type = checkbox.dataset.highlightCountryType;

    if (type === 'crisis') {
      if (isNowChecked) {
        this.overlay.classList.remove(CLASS_NAMES.HIDDEN);
      } else {
        this.overlay.classList.add(CLASS_NAMES.HIDDEN);
      }
    } else {
      const highlightedCountries = this.mapCountry.querySelectorAll(
        `[data-highlight-country-type="${type}"]`
      );
      highlightedCountries.forEach((country) => {
        if (isNowChecked) {
          country.classList.add(type);
        } else {
          country.classList.remove(type);
        }
      });
    }
  }

  /**
   * Add tooltip functionality
   * @returns {WorkMap} A reference to the current instance of the class
   * @chainable
   */
  initializeTooltips() {
    this.tooltip = this.element.querySelector(`#${CLASS_NAMES.MAP_TOOLTIP}`);
    this.activeTooltip = null;

    this.landPaths.forEach((country) => {
      const iso = country.id;
      const tooltipContent = this.element.querySelector(`#${iso}--country`);

      if (tooltipContent) {
        country.addEventListener(EVENTS.CLICK, (e) => {
          // Hide any existing tooltip
          if (this.activeTooltip) {
            this.hideTooltip();
          }

          const mapBounds = this.mapCon.getBoundingClientRect();
          let countryBounds = country.getBoundingClientRect();

          // Find largest sub-area for multi-path countries
          let largestBoundSubArea = 0;
          country.querySelectorAll('path').forEach((path) => {
            const bounds = path.getBoundingClientRect();
            const boundSubArea = bounds.width * bounds.height;

            if (boundSubArea > largestBoundSubArea) {
              largestBoundSubArea = boundSubArea;
              countryBounds = bounds;
            }
          });

          // Position tooltip
          const tooltipLeft = countryBounds.right - mapBounds.left + 10;
          const tooltipTop = countryBounds.top - mapBounds.top;

          this.showTooltip(tooltipContent.innerHTML, tooltipLeft, tooltipTop);
          country.classList.add(CLASS_NAMES.SELECTED);
          this.activeTooltip = {
            country,
            content: tooltipContent
          };

          e.stopPropagation();
        });
      }
    });

    document.addEventListener(EVENTS.CLICK, (e) => {
      if (!this.tooltip.contains(e.target)) {
        this.hideTooltip();
      }
    });

    return this;
  }

  /**
   * Show tooltip with content at specified position
   * @param {string} content - HTML content for tooltip
   * @param {number} left - Left position
   * @param {number} top - Top position
   */
  showTooltip(content, left, top) {
    this.tooltip.innerHTML = `
      <div class="tooltip__content">
        ${content}
        <button class="work-map-tooltip__close" aria-label="Close tooltip">&times;</button>
      </div>
    `;

    this.tooltip.style.left = `${left}px`;
    this.tooltip.style.top = `${top}px`;
    this.tooltip.classList.add(CLASS_NAMES.VISIBLE);

    this.tooltip
      .querySelector(CLASS_NAMES.MAP_CLOSE)
      .addEventListener('click', () => {
        this.hideTooltip();
      });
  }

  /**
   * Hide active tooltip
   */
  hideTooltip() {
    if (this.activeTooltip) {
      this.tooltip.classList.remove(CLASS_NAMES.VISIBLE);
      this.tooltip.innerHTML = '';
      this.activeTooltip.country.classList.remove(CLASS_NAMES.SELECTED);
      this.activeTooltip = null;
    }
  }

  /**
   * Handle window resize events
   */
  handleResize() {
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }

    this.resizeTimeout = window.requestAnimationFrame(() => {
      this.processCountriesMarkerOnLoad();

      if (this.activeTooltip) {
        const { country } = this.activeTooltip;
        const mapBounds = this.mapCon.getBoundingClientRect();
        let countryBounds = country.getBoundingClientRect();
        let largestBoundSubArea = 0;

        country.querySelectorAll('path').forEach((path) => {
          const bounds = path.getBoundingClientRect();
          const boundSubArea = bounds.width * bounds.height;

          if (boundSubArea > largestBoundSubArea) {
            largestBoundSubArea = boundSubArea;
            countryBounds = bounds;
          }
        });

        const tooltipLeft = countryBounds.right - mapBounds.left + 10;
        const tooltipTop = countryBounds.top - mapBounds.top;

        this.tooltip.style.left = `${tooltipLeft}px`;
        this.tooltip.style.top = `${tooltipTop}px`;
      }
    });
  }

  /**
   * Cleanup method to remove event listeners
   */
  destroy() {
    window.removeEventListener(EVENTS.RESIZE, this.handleResize);
    if (this.resizeTimeout) {
      window.cancelAnimationFrame(this.resizeTimeout);
    }
  }
}
